<template>
  <!-- Bundle group: self-contained card so it's clearly distinct from sections -->
  <div
    class="rounded-xl border overflow-hidden"
    :class="group.all_returned ? 'border-[var(--color-border)] opacity-60' : 'border-[var(--color-accent)]/40'"
  >
    <!-- Clickable header -->
    <button
      class="w-full flex items-center gap-2 px-4 py-2.5 text-left cursor-pointer select-none transition-colors"
      :class="group.all_returned ? 'bg-[var(--color-surface-2)]' : 'bg-[var(--color-accent)]/10 hover:bg-[var(--color-accent)]/15'"
      @click="collapsed = !collapsed"
    >
      <!-- Collapse chevron -->
      <MdiChevronDown v-if="!collapsed" class="w-4 h-4 shrink-0 text-[var(--color-accent)]" />
      <MdiChevronRight v-else class="w-4 h-4 shrink-0 text-[var(--color-accent)]" />

      <!-- Bundle icon -->
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0 text-[var(--color-accent)]">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>

      <!-- Name + SKU -->
      <span class="flex-1 font-semibold text-sm text-[var(--color-text)] truncate">
        {{ group.bundle.name }}
      </span>
      <span v-if="group.bundle.sku" class="text-xs text-[var(--color-muted)] font-mono shrink-0">
        {{ group.bundle.sku }}
      </span>

      <!-- Returned badge -->
      <span v-if="group.all_returned" class="badge badge-success shrink-0">{{ $t('bundles.groupStatus.1') }}</span>
      <span v-else-if="statusLabel" class="badge badge-muted shrink-0">{{ statusLabel }}</span>

      <!-- Expected check-in date -->
      <span
        v-if="group.expected_checkin"
        class="text-xs text-[var(--color-muted)] shrink-0"
        :title="$t('ui.expectedReturnDate', { date: formatDate(group.expected_checkin) })"
      >{{ formatDate(group.expected_checkin) }}</span>

      <!-- Item count pill -->
      <span class="badge badge-muted badge-pill shrink-0">{{ group.items.length }}</span>
    </button>

    <!-- Items — inside the card, separated by a top border -->
    <div
      v-show="!collapsed"
      class="flex flex-col gap-2 p-2 border-t border-[var(--color-border)]"
    >
      <slot />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import MdiChevronDown from '~icons/mdi/chevron-down'
import MdiChevronRight from '~icons/mdi/chevron-right'

const { t } = useI18n()

const props = defineProps({
  group: {
    type: Object,
    required: true
  }
})

const collapsed = ref(false)

const statusLabel = computed(() => {
  if (props.group.all_returned) return null
  return t(`bundles.groupStatus.${props.group.status}`, props.group.status?.toString() ?? '')
})

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric' })
}
</script>
